import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ArrowLeftSLineIcon from "remixicon-react/ArrowLeftSLineIcon";
import SideNavbar from '../../components/Navbar/SideNavbar';
import Header from '../../components/Header/Header';
import {
    createDynamicPricing,
    updateDynamicPricing,
    getDynamicPricingById,
    setDynamicPricingDetails
} from '../../../core/actionCreators/dynamicPricingActionCreator';
import Loader from '../../components/Loader/Loader';
import { toast } from 'react-hot-toast';

// Single source of truth for the competitors: drives both the primary-competitor
// dropdown and the URL / competitiveness fields, so the two can never drift apart.
const COMPETITORS = [
    { value: "Amazon", label: "Amazon", urlKey: "amazonUrl", percentageKey: "amazonCompetitivenessPercentage" },
    { value: "Flipkart", label: "Flipkart", urlKey: "flipkartUrl", percentageKey: "flipkartCompetitivenessPercentage" },
    { value: "IndustryBuying", label: "Industry Buying", urlKey: "industryBuyingUrl", percentageKey: "ibCompetitivenessPercentage" },
    { value: "Croma", label: "Croma", urlKey: "cromaUrl", percentageKey: "cromaCompetitivenessPercentage" },
    { value: "Reliance", label: "Reliance Digital", urlKey: "relianceDigitalUrl", percentageKey: "relianceCompetitivenessPercentage" },
    { value: "TataCliq", label: "Tata Cliq", urlKey: "tataCliqUrl", percentageKey: "tatacliqCompetitivenessPercentage" },
];

const LABEL_CLASS = "block text-xs mb-1.5 text-[#5E5D5D] font-medium";
const INPUT_CLASS = "w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light focus:border-blue-400 disabled:bg-gray-100";
const SELECT_CLASS = "bg-white text-xs border text-[#5E5D5D] border-[#EAEAEA] h-9 px-3 rounded w-full outline-none focus:border-blue-400";
const PANEL_CLASS = "w-full bg-white shadow-[0_1px_4px_rgba(0,0,0,.2)] rounded mb-5";
const PANEL_TITLE_CLASS = "bg-[#f6f8f8] p-4 text-base border-b border-b-[#efeff4]";

const DynamicPricingFormPage = () => {
    const { msn } = useParams<{ msn?: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isEdit = !!msn;

    const { isLoading, dynamicPricingDetails } = useSelector((s: any) => s.dynamicPricingStore);

    const [formData, setFormData] = useState<any>({
        productRef: "",
        productName: "",
        category: "",
        categoryCode: 0,
        brandId: "",
        cluster: "",
        amazonUrl: "",
        amazonCompetitivenessPercentage: 0,
        flipkartUrl: "",
        flipkartCompetitivenessPercentage: 0,
        industryBuyingUrl: "",
        ibCompetitivenessPercentage: 0,
        cromaUrl: "",
        cromaCompetitivenessPercentage: 0,
        relianceDigitalUrl: "",
        relianceCompetitivenessPercentage: 0,
        tataCliqUrl: "",
        tatacliqCompetitivenessPercentage: 0,
        primaryCompetitor: "",
        minGmPercentage: 0,
        maxGmPercentage: 0,
        dynamicPricingEnabled: true
    });

    useEffect(() => {
        if (isEdit && msn) {
            dispatch(getDynamicPricingById(msn, (data: any) => {
                if (!data) {
                    toast.error("Mapping not found");
                    navigate("/dynamic-pricing");
                }
            }));
        } else {
            dispatch(setDynamicPricingDetails({}));
        }
    }, [isEdit, msn]);

    useEffect(() => {
        if (isEdit && dynamicPricingDetails && Object.keys(dynamicPricingDetails).length > 0) {
            setFormData(dynamicPricingDetails);
        }
    }, [dynamicPricingDetails]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        // An emptied number field must stay empty — parseFloat("") is NaN, which
        // would be submitted as null.
        const val = type === 'number' ? (value === "" ? "" : parseFloat(value)) : value;

        setFormData({
            ...formData,
            [name]: val
        });
    };

    const backToListing = () => navigate("/dynamic-pricing");

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.productRef || !formData.productName) {
            toast.error("Product Reference and Name are required");
            return;
        }

        const action = isEdit ? updateDynamicPricing : createDynamicPricing;

        dispatch(action(formData, (success: boolean) => {
            if (success) {
                navigate("/dynamic-pricing");
            }
        }));
    };

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container">
                    <div className="Subheader bg-white p-[15px] shadow-[0_2px_6px_rgba(0,0,0,.1)]">
                        <p className="text-xl font-bold text-[#43425D] flex items-center">
                            <ArrowLeftSLineIcon onClick={backToListing} size={24} className="mr-4 cursor-pointer" />
                            {isEdit ? `Update Mapping : ${msn}` : "Create Mapping"}
                        </p>
                    </div>

                    {isLoading && isEdit && !formData.productRef ? (
                        <div className="p-4"><Loader /></div>
                    ) : (
                        <form onSubmit={handleSubmit} className="p-4">
                            <div className={PANEL_CLASS}>
                                <p className={PANEL_TITLE_CLASS}>Basic Information</p>
                                <div className="py-4 px-2.5">
                                    <div className="flex w-full mb-6">
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Product Ref (MSN)*:</label>
                                            <input
                                                type="text"
                                                name="productRef"
                                                value={formData.productRef}
                                                onChange={handleChange}
                                                disabled={isEdit}
                                                placeholder="Enter MSN"
                                                className={INPUT_CLASS}
                                                required
                                            />
                                        </div>
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Product Name*:</label>
                                            <input
                                                type="text"
                                                name="productName"
                                                value={formData.productName}
                                                onChange={handleChange}
                                                placeholder="Enter product name"
                                                className={INPUT_CLASS}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="flex w-full mb-6">
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Category:</label>
                                            <input
                                                type="text"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleChange}
                                                placeholder="Enter category"
                                                className={INPUT_CLASS}
                                            />
                                        </div>
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Category Code:</label>
                                            <input
                                                type="number"
                                                name="categoryCode"
                                                value={formData.categoryCode}
                                                onChange={handleChange}
                                                placeholder="Enter category code"
                                                className={INPUT_CLASS}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex w-full">
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Brand ID:</label>
                                            <input
                                                type="text"
                                                name="brandId"
                                                value={formData.brandId}
                                                onChange={handleChange}
                                                placeholder="Enter brand ID"
                                                className={INPUT_CLASS}
                                            />
                                        </div>
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Cluster:</label>
                                            <input
                                                type="text"
                                                name="cluster"
                                                value={formData.cluster}
                                                onChange={handleChange}
                                                placeholder="Enter cluster"
                                                className={INPUT_CLASS}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={PANEL_CLASS}>
                                <p className={PANEL_TITLE_CLASS}>Pricing Configuration</p>
                                <div className="py-4 px-2.5">
                                    <div className="flex w-full mb-6">
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Primary Competitor:</label>
                                            <select
                                                name="primaryCompetitor"
                                                value={formData.primaryCompetitor}
                                                onChange={handleChange}
                                                className={SELECT_CLASS}
                                            >
                                                <option value="">Select</option>
                                                {COMPETITORS.map((competitor) => (
                                                    <option key={competitor.value} value={competitor.value}>{competitor.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Dynamic Pricing:</label>
                                            <select
                                                name="dynamicPricingEnabled"
                                                value={formData.dynamicPricingEnabled ? "true" : "false"}
                                                onChange={(e) => setFormData({ ...formData, dynamicPricingEnabled: e.target.value === "true" })}
                                                className={SELECT_CLASS}
                                            >
                                                <option value="true">Enabled</option>
                                                <option value="false">Disabled</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex w-full">
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Min GM %:</label>
                                            <input
                                                type="number"
                                                name="minGmPercentage"
                                                step="0.1"
                                                value={formData.minGmPercentage}
                                                onChange={handleChange}
                                                className={INPUT_CLASS}
                                            />
                                        </div>
                                        <div className="w-1/2 px-2">
                                            <label className={LABEL_CLASS}>Max GM %:</label>
                                            <input
                                                type="number"
                                                name="maxGmPercentage"
                                                step="0.1"
                                                value={formData.maxGmPercentage}
                                                onChange={handleChange}
                                                className={INPUT_CLASS}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={PANEL_CLASS}>
                                <p className={PANEL_TITLE_CLASS}>Competitor URLs</p>
                                <div className="py-4 px-2.5">
                                    {COMPETITORS.map((competitor) => (
                                        <div key={competitor.value} className="flex w-full mb-6 last:mb-0 items-end">
                                            <div className="w-1/2 px-2">
                                                <label className={LABEL_CLASS}>
                                                    {competitor.label} URL:
                                                    {formData.primaryCompetitor === competitor.value && (
                                                        <span className="ml-2 text-[10px] font-medium text-blue-600">PRIMARY</span>
                                                    )}
                                                </label>
                                                <input
                                                    type="text"
                                                    name={competitor.urlKey}
                                                    value={formData[competitor.urlKey] || ""}
                                                    onChange={handleChange}
                                                    placeholder={`Enter ${competitor.label} product URL`}
                                                    className={INPUT_CLASS}
                                                />
                                            </div>
                                            <div className="w-1/2 px-2">
                                                <label className={LABEL_CLASS}>{competitor.label} Comp %:</label>
                                                <input
                                                    type="number"
                                                    name={competitor.percentageKey}
                                                    step="0.1"
                                                    value={formData[competitor.percentageKey] ?? 0}
                                                    onChange={handleChange}
                                                    className={INPUT_CLASS}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="bg-blue-500 text-white px-8 rounded h-9 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {isLoading ? "Saving..." : (isEdit ? "Update Mapping" : "Create Mapping")}
                                </button>
                                <button
                                    type="button"
                                    onClick={backToListing}
                                    className="h-9 px-6 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DynamicPricingFormPage;
